import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import BaseCard from './BaseCard';
import Rare999Card from './Rare999Card';
import RareAutoCard from './RareAutoCard';
import DualCard from './DualCard';
import RarePatchCard from './RarePatchCard';
import LegendaryPatchAutoCard from './LegendaryPatchAutoCard';
import ChromeAutoCard from './ChromeAutoCard';
import AutoPatchCard from './AutoPatchCard';
import { calculateCardValue, formatValue, isJerseyMatch, getDailyMarket, applyMarket } from '../../data/cardValues';
import './cards.css';

const RARE_TYPES = new Set(['rare_999', 'rare_dual', 'rare_patch', 'rare_auto', 'chrome_auto', 'auto_patch', 'patch_auto', 'dual_patch']);

const PackReveal = ({ cards, packType, onClose, onOpenAnother, onSellCards }) => {
  const [loading, setLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showingBack, setShowingBack] = useState(() => RARE_TYPES.has(cards[0]?.card_type));
  const [noTransition, setNoTransition] = useState(() => RARE_TYPES.has(cards[0]?.card_type));
  const [animating, setAnimating] = useState(false);
  const [slideOut, setSlideOut] = useState(false);
  const [done, setDone] = useState(false);
  const [showValue, setShowValue] = useState(!RARE_TYPES.has(cards[0]?.card_type));
  const [sellSelection, setSellSelection] = useState(new Set());
  const [sellingCards, setSellingCards] = useState(false);
  const frameRef = useRef(null);
  const preRenderRef = useRef(null);

  // Phase 1: preload images into browser cache, then render all cards hidden,
  // then wait for the actual <img> elements in the DOM to finish painting.
  useEffect(() => {
    // Collect all image URLs the cards will use
    const urls = [];
    cards.forEach(c => {
      const d = c.card_data;
      if (!d) return;
      const addPlayer = (p) => {
        const sport = p.sport || 'mlb';
        if (p.photoUrl) urls.push(p.photoUrl);
        else if (p.playerId) urls.push(sport === 'nhl'
          ? `https://assets.nhle.com/mugs/actionshots/1296x729/${p.playerId}.jpg`
          : `./cards/photos/players/${p.playerId}.jpg`);
        if (p.playerId) urls.push(sport === 'nhl'
          ? `https://assets.nhle.com/mugs/nhl/latest/${p.playerId}.png`
          : `https://img.mlbstatic.com/mlb-photos/image/upload/w_200,q_auto:best/v1/people/${p.playerId}/headshot/silo/current`);
        if (p.teamLogoId) urls.push(`https://www.mlbstatic.com/team-logos/${p.teamLogoId}.svg`);
        if (sport === 'nhl' && p.team) urls.push(`https://assets.nhle.com/logos/nhl/svg/${p.team}_light.svg`);
      };
      if (d.playerId) addPlayer(d);
      if (d.player1) addPlayer(d.player1);
      if (d.player2) addPlayer(d.player2);
      if (['rare_patch', 'patch_auto', 'auto_patch'].includes(c.card_type)) urls.push('./cards/photos/fabric.jpg');
      if (d.autoImage) urls.push(d.autoImage);
      if (d.auto1Image) urls.push(d.auto1Image);
      if (d.auto2Image) urls.push(d.auto2Image);
    });
    const unique = [...new Set(urls)];
    if (unique.length === 0) { setLoading(false); return; }

    let loaded = 0;
    const total = unique.length;
    const onLoad = () => {
      loaded++;
      setLoadProgress(Math.round((loaded / total) * 100));
      if (loaded >= total) {
        // Images cached — wait 2 frames for the pre-rendered hidden cards to paint
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setLoading(false);
          });
        });
      }
    };
    const timeout = setTimeout(() => setLoading(false), 10000);
    unique.forEach(url => { const img = new Image(); img.onload = onLoad; img.onerror = onLoad; img.src = url; });
    return () => clearTimeout(timeout);
  }, [cards]);

  const isRare = (cardType) => RARE_TYPES.has(cardType);

  const currentCard = cards[currentIndex];
  const currentIsRare = currentCard && isRare(currentCard.card_type);
  const isLastCard = currentIndex === cards.length - 1;

  // Pre-calculate all card values
  const cardValues = useMemo(() =>
    cards.map(c => calculateCardValue(c.card_data, c.card_type, c.numbering)),
    [cards]
  );

  const totalValue = useMemo(() => cardValues.reduce((sum, v) => sum + v, 0), [cardValues]);
  const market = useMemo(() => getDailyMarket(), []);
  const sellValues = useMemo(() => cardValues.map(v => Math.round(applyMarket(v, market) * 0.8)), [cardValues, market]);
  const sellTotal = useMemo(() => [...sellSelection].reduce((sum, i) => sum + sellValues[i], 0), [sellSelection, sellValues]);

  const revealedTotal = useMemo(() =>
    cardValues.slice(0, currentIndex + (showValue ? 1 : 0)).reduce((sum, v) => sum + v, 0),
    [cardValues, currentIndex, showValue]
  );

  const currentValue = cardValues[currentIndex];

  useEffect(() => {
    if (noTransition) {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = requestAnimationFrame(() => {
          setNoTransition(false);
        });
      });
    }
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [currentIndex, noTransition]);

  // Spacebar triggers same action as click
  useEffect(() => {
    const onKey = (e) => { if (e.code === 'Space') { e.preventDefault(); handleClick(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const handleClick = useCallback(() => {
    if (animating || done) return;

    if (currentIsRare && showingBack) {
      setShowingBack(false);
      setShowValue(true);
      return;
    }

    if (isLastCard) {
      setDone(true);
      return;
    }

    setAnimating(true);
    setSlideOut(true);
    setTimeout(() => {
      const nextIndex = currentIndex + 1;
      const nextIsRare = RARE_TYPES.has(cards[nextIndex]?.card_type);
      setCurrentIndex(nextIndex);
      setShowingBack(nextIsRare);
      setNoTransition(nextIsRare);
      setShowValue(!nextIsRare);
      setSlideOut(false);
      setAnimating(false);
    }, 350);
  }, [animating, done, currentIsRare, showingBack, isLastCard, currentIndex, cards]);

  const renderCard = (card, chromeInteractive = false) => {
    const cardData = { ...card.card_data, numbering: card.numbering };
    const ct = card.card_type;
    const showBack = true;

    if (ct === 'rare_dual') return <DualCard card={cardData} interactive={chromeInteractive} showBack={showBack} />;
    if (ct === 'dual_patch') return <DualCard card={{ ...cardData, legendary: true }} interactive={chromeInteractive} showBack={showBack} />;
    if (ct === 'rare_patch') return <RarePatchCard card={cardData} interactive={chromeInteractive} showBack={showBack} />;
    if (ct === 'auto_patch') return <AutoPatchCard card={cardData} interactive={chromeInteractive} showBack={showBack} />;
    if (ct === 'patch_auto') return <LegendaryPatchAutoCard card={cardData} interactive={chromeInteractive} showBack={showBack} />;
    if (ct === 'chrome_auto') return <ChromeAutoCard player={cardData} interactive={chromeInteractive} showBack={showBack} />;
    if (ct === 'rare_auto') return <RareAutoCard player={cardData} interactive={chromeInteractive} showBack={showBack} />;
    if (ct === 'rare_999') return <Rare999Card player={cardData} interactive={chromeInteractive} showBack={showBack} />;
    return <BaseCard player={cardData} interactive={chromeInteractive} showBack={showBack} />;
  };

  const cardCounter = `${currentIndex + 1} / ${cards.length}`;

  const wrapperClass = [
    showingBack ? 'pack-card-force-back' : '',
    noTransition ? 'pack-card-no-transition' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="pack-overlay" onClick={(e) => { if (e.target === e.currentTarget && done) onClose(); }}>
      {/* Pre-render ALL cards off-screen so the browser paints images from cache.
          This eliminates the colored background flash when switching cards. */}
      <div ref={preRenderRef} style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden', pointerEvents: 'none' }} aria-hidden="true">
        {cards.map((card, i) => (
          <div key={i}>{renderCard(card, false)}</div>
        ))}
      </div>

      {loading ? (
        <div className="pack-loading">
          <div className="pack-loading-spinner" />
          <p>Opening pack...{loadProgress > 0 && loadProgress < 100 ? ` ${loadProgress}%` : ''}</p>
        </div>
      ) : (
        <div className="pack-stack-container">
          <div className="pack-stack-header">
            <h2 className="pack-reveal-title">
              {done ? 'Pack Complete!' : `${packType.charAt(0).toUpperCase() + packType.slice(1)} Pack`}
            </h2>
            <p className="pack-reveal-hint">
              {done ? '' : currentIsRare && showingBack ? 'Click to reveal!' : `Card ${cardCounter} — Click for next`}
            </p>
            {!done && showValue && (
              <div className="pack-running-total">
                Pack Value: <span>{formatValue(revealedTotal)}</span>
              </div>
            )}
          </div>

          {!done && currentCard && (
            <div
              key={currentIndex}
              className={`pack-stack-card ${slideOut ? 'pack-slide-out' : 'pack-slide-in'}`}
              onClick={handleClick}
            >
              {currentIndex < cards.length - 1 && (
                <>
                  <div className="pack-stack-shadow pack-shadow-1" />
                  {currentIndex < cards.length - 2 && <div className="pack-stack-shadow pack-shadow-2" />}
                </>
              )}

              <div className={wrapperClass || undefined}>
                {renderCard(currentCard, !showingBack)}
              </div>

              {showValue && (
                <div className={`pack-value-badge ${currentValue >= 50 ? 'pack-value-high' : currentValue >= 10 ? 'pack-value-mid' : ''}`}>
                  {formatValue(currentValue)}
                  {isJerseyMatch(currentCard.card_data, currentCard.numbering) && (
                    <span className="pack-jersey-match">JERSEY MATCH! x3</span>
                  )}
                </div>
              )}
            </div>
          )}

          {done && (
            <>
              <div className="pack-total-value">
                Total Pack Value: <span>{formatValue(totalValue)}</span>
              </div>

              {/* Buyback grid */}
              <div className="pack-buyback-grid">
                {cards.map((card, i) => {
                  const selected = sellSelection.has(i);
                  return (
                    <div key={i} className={`pack-buyback-card ${selected ? 'pack-buyback-selected' : ''}`} onClick={() => {
                      setSellSelection(prev => {
                        const next = new Set(prev);
                        if (next.has(i)) next.delete(i); else next.add(i);
                        return next;
                      });
                    }}>
                      <div className="pack-buyback-wrapper">
                        <div className={`pack-buyback-inner ${['rare_dual','dual_patch','rare_patch','auto_patch'].includes(card.card_type) ? 'pack-buyback-inner-h' : ''}`}>
                          {renderCard(card, false)}
                        </div>
                      </div>
                      <div className="pack-buyback-info">
                        <div className="pack-buyback-name">{card.card_data?.name || card.card_data?.player1?.name || '???'}</div>
                        <div className="pack-buyback-val">{formatValue(cardValues[i])}</div>
                        <div className="pack-buyback-sell">Sell: {formatValue(sellValues[i])}</div>
                      </div>
                      {selected && <div className="pack-buyback-check">SELL</div>}
                    </div>
                  );
                })}
              </div>

              {sellSelection.size > 0 && (
                <div className="pack-buyback-bar">
                  Sell {sellSelection.size} card{sellSelection.size > 1 ? 's' : ''} for <b>{formatValue(sellTotal)}</b>
                  <button className="pack-btn pack-btn-sell" disabled={sellingCards} onClick={async () => {
                    if (!onSellCards) return;
                    setSellingCards(true);
                    const ids = [...sellSelection].map(i => cards[i].user_card_id).filter(Boolean);
                    if (ids.length > 0) await onSellCards(ids, sellTotal);
                    setSellingCards(false);
                    setSellSelection(new Set());
                  }}>{sellingCards ? 'Selling...' : 'Sell Selected'}</button>
                </div>
              )}

              <div className="pack-complete-actions">
                <button className="pack-btn pack-btn-primary" onClick={onOpenAnother}>Open Another</button>
                <button className="pack-btn pack-btn-secondary" onClick={onClose}>Keep All & Close</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PackReveal;
